// main tools
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'

// components
import { PageLoader } from '@centria-ui-atoms/spinner/templates/page-loader'
import { AppLayout } from '@centria-ui-molecules/layout'

// hooks
import { useApollo } from '@dr-keyword-gql/apollo'

// providers
import { NetworkProvider } from '@centria-ui/contexts/network/provider'
import { ApolloProvider } from '@apollo/client'

// utils
import '../lib/i18n-settings'

// styles
import '@dr-keyword-styles/styles.scss'

// types
import type { NormalizedCacheObject } from '@apollo/client'
import type { AppProps } from 'next/app'
import type { Session } from 'next-auth'
import type { NextPage } from 'next'

type MyAppProps = {
  session: Session
  initialApolloState: NormalizedCacheObject
}

const MyApp: NextPage<AppProps<MyAppProps>> = ({
  Component,
  pageProps: { session, initialApolloState, ...pageProps }
}) => {
  const REFETCH_MINUTES = 5
  const REFETCH_SECONDS = 60
  const { locale } = useRouter()
  const { i18n } = useTranslation()
  const [ready, setReady] = useState(false)
  const apolloClient = useApollo(initialApolloState)

  useEffect(() => {
    i18n.changeLanguage(locale)
    setReady(true)
  }, [locale])

  return (
    <ApolloProvider client={apolloClient}>
      <AppLayout
        session={session}
        title='Dr. Keyword'
        refetchInterval={REFETCH_SECONDS * REFETCH_MINUTES} // each five minutes
        logoSpinner={<PageLoader icon='DrKeywordCircleIcon' />}>
        <main>
          {ready && (
            <NetworkProvider>
              <Component {...pageProps} />
            </NetworkProvider>
          )}
        </main>
      </AppLayout>
    </ApolloProvider>
  )
}

export default MyApp
